// client/src/components/ProjectScorecard.js
// Project-level KPI scorecard — the landing page when a user clicks a project.
// Fetches GET /api/projects/:id/scorecard and renders aggregate tiles across
// all campaigns in the project.
//
// Layout (UPDATED Aug 2026):
//   Row 1 — Minimal header: Icon roundel + project name (inline)
//   Row 2 — Horizontal metrics overview (Visibility, Citation, Position, etc.)
//   Row 3 — Two-column: Project stats (L) | Performance (R)
//   Row 4 — Two-column: Campaign cards grid (L) | Trends chart (R)
//   Row 5 — Engine chart — full width
//   Row 6 — Project members — full width
//
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import ProjectMembersPanel from './ProjectMembersPanel';
import { DeltaBadge } from './DeltaBadge';

const C = {
  primary:   '#39393a',
  secondary: '#048ba8',
  tertiary:  '#e9c46a',
  grey:      '#e76f51',
  green:     '#01796f',
  textMuted: '#6b7280',
  border:    '#e5e7eb',
  lightbg:   '#f9fafb',
  green_bg:  'rgba(1, 121, 111, 0.12)',
  amber_bg:  'rgba(233, 196, 106, 0.12)',
  red_bg:    'rgba(231, 111, 81, 0.12)',
  green_tx:  '#01796f',
  amber_tx:  '#e9c46a',
  red_tx:    '#e76f51',
};

// Chart colour palette
const CHART = {
  visibility: '#048ba8',
  citation:   '#01796f',
  errorRate:  '#e76f51',
  sentiment:  '#e9c46a',
};

const ENGINE_COLORS = {
  chatgpt:    '#10a37f',
  claude:     '#c084fc',
  gemini:     '#4285f4',
  perplexity: '#f59e0b',
};

// Helper: extract initials from project name
function getInitials(name) {
  if (!name) return '?';
  const words = name.trim().split(/\s+/);
  return words.length > 1
    ? (words[0][0] + words[words.length - 1][0]).toUpperCase()
    : name.substring(0, 2).toUpperCase();
}

function scoreColor(val, invert = false) {
  const good   = invert ? val <= 2  : val >= 70;
  const medium = invert ? val <= 5  : val >= 40;
  if (good)   return { tx: C.green_tx, bg: C.green_bg };
  if (medium) return { tx: C.amber_tx, bg: C.amber_bg };
  return       { tx: C.red_tx,   bg: C.red_bg };
}

function getScoreColor(score, type = 'default') {
  if (score === null) return C.primary;
  if (type === 'position') {
    if (score <= 2) return C.green;
    if (score <= 5) return C.secondary;
    if (score <= 8) return C.tertiary;
    return C.grey;
  }
  if (score >= 70) return C.green;
  if (score >= 45) return C.tertiary;
  return C.grey;
}

function formatMetricValue(value, type) {
  if (value === null) return 'N/A';
  switch (type) {
    case 'visibility':
    case 'citation':
    case 'content':
    case 'technical':
    case 'entityGap':
    case 'knowledgeGraph':
      return `${Math.round(value)}%`;
    case 'position':
      return value.toFixed(1);
    default:
      return value;
  }
}

function Tile({ label, value, suffix = '', invert = false, isCount = false, onClick }) {
  const isNull = value === null || value === undefined;
  let colorStyle = { tx: C.textMuted, bg: 'white' };
  if (!isNull && !isCount) colorStyle = scoreColor(value, invert);
  const displayValue = isNull
    ? '—'
    : isCount
      ? value.toLocaleString()
      : `${Math.round(value)}${suffix}`;
  return (
    <div
      onClick={onClick}
      style={{
        backgroundColor: isCount ? 'white' : colorStyle.bg,
        border: `1px solid ${C.border}`,
        borderRadius: '10px',
        padding: '24px 20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'box-shadow 0.15s',
      }}
      onMouseEnter={e => { if (onClick) e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
    >
      <div style={{ fontSize: '11px', fontWeight: '600', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.7px' }}>
        {label}
      </div>
      <div style={{
        fontSize: isCount ? '32px' : '40px',
        fontWeight: '800',
        color: isCount ? C.primary : (isNull ? '#d1d5db' : colorStyle.tx),
        lineHeight: 1,
      }}>
        {displayValue}
      </div>
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div style={{
      fontSize: '11px',
      fontWeight: '700',
      color: C.secondary,
      textTransform: 'uppercase',
      letterSpacing: '0.8px',
      marginTop: '32px',
      marginBottom: '12px',
      paddingBottom: '8px',
      borderBottom: `1px solid ${C.border}`,
    }}>
      {children}
    </div>
  );
}

// Compact campaign card for grid display
function CampaignCard({ campaign, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        backgroundColor: 'white',
        border: `1px solid ${C.border}`,
        borderRadius: '8px',
        padding: '16px',
        cursor: 'pointer',
        transition: 'all 0.15s',
        minHeight: '120px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
        e.currentTarget.style.borderColor = C.secondary;
      }}
      onMouseLeave={e => {
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.borderColor = C.border;
      }}
    >
      <div>
        <div style={{
          fontSize: '13px',
          fontWeight: '600',
          color: C.primary,
          marginBottom: '4px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}>
          {campaign.name}
        </div>
        {campaign.brand_name && (
          <div style={{
            fontSize: '11px',
            color: C.textMuted,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            {campaign.brand_name}
          </div>
        )}
      </div>
      <div style={{
        fontSize: '12px',
        color: C.textMuted,
        marginTop: '8px',
      }}>
        {campaign.urls?.length || 0} URL{campaign.urls?.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
}

// Custom tooltip for line chart
function TimelineTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div style={{
      backgroundColor: 'white',
      border: `1px solid ${C.border}`,
      borderRadius: '8px',
      padding: '10px 14px',
      fontSize: '12px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    }}>
      <div style={{ fontWeight: '700', color: C.primary, marginBottom: '6px' }}>{label}</div>
      {payload.map(p => (
        <div key={p.dataKey} style={{ color: p.color, display: 'flex', gap: '8px', alignItems: 'center' }}>
          <span style={{ fontWeight: '600' }}>{p.name}:</span>
          <span>{p.value !== null && p.value !== undefined ? `${Math.round(p.value)}%` : '—'}</span>
        </div>
      ))}
    </div>
  );
}

// Custom tooltip for bar chart
function EngineTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div style={{
      backgroundColor: 'white',
      border: `1px solid ${C.border}`,
      borderRadius: '8px',
      padding: '10px 14px',
      fontSize: '12px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    }}>
      <div style={{ fontWeight: '700', color: C.primary, marginBottom: '4px', textTransform: 'capitalize' }}>{label}</div>
      <div style={{ color: payload[0].color, fontWeight: '600' }}>
        Visibility: {payload[0].value}%
      </div>
    </div>
  );
}

// ── Extract per-run history from llm-audit result_data blobs ────────────────
function extractRunMetrics(resultData, createdAt) {
  if (!resultData) return null;
  const summary = resultData.summary || {};
  const brandMetrics = Array.isArray(resultData.brandMetrics)
    ? resultData.brandMetrics
    : Array.isArray(summary.brandMetrics)
      ? summary.brandMetrics
      : [];
  const target = brandMetrics.find(b => b && b.isTarget);
  const visibility = target?.visibilityScore ?? summary.brandMentionRate ?? null;
  const citation   = typeof resultData.citationRate === 'number'
    ? resultData.citationRate
    : target?.citationScore ?? summary.citationRate ?? null;
  const sentiment  = (target && Array.isArray(target.sentiments) && target.sentiments.length > 0)
    ? target.averageSentiment
    : typeof summary.sentimentScore === 'number'
      ? summary.sentimentScore
      : null;
  const ep = summary.enginePerformance || {};
  const engines = {};
  for (const [engine, data] of Object.entries(ep)) {
    engines[engine] = typeof data.brandMentionRate === 'number' ? data.brandMentionRate : null;
  }
  const date = new Date(createdAt);
  const label = date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  return { date: createdAt, label, visibility, citation, sentiment, engines };
}

// ── Main component ───────────────────────────────────────────────────────────
function ProjectScorecard({ apiBaseUrl, userId }) {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();
  const clerkUserId = user?.id || userId;
  const [scorecard, setScorecard]           = useState(null);
  const [project, setProject]               = useState(null);
  const [campaigns, setCampaigns]           = useState([]);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState(null);
  const [timelineData, setTimelineData]     = useState([]);
  const [engineData, setEngineData]         = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [isAdmin, setIsAdmin]               = useState(false);
  const [deltas, setDeltas]                 = useState({});

  // Fetch scorecard
  useEffect(() => {
    if (!projectId || !apiBaseUrl) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${apiBaseUrl}/projects/${projectId}/scorecard`, {
          headers: { 'x-user-id': clerkUserId || '' },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!json.success) throw new Error(json.error || 'Failed to load scorecard');
        setProject(json.project);
        setScorecard(json.scorecard);
        if (json.scorecard.deltas) {
          setDeltas(json.scorecard.deltas);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [projectId, apiBaseUrl, clerkUserId]);

  // Fetch campaigns for this project
  useEffect(() => {
    if (!projectId || !apiBaseUrl || !clerkUserId) return;
    const fetchCampaigns = async () => {
      try {
        const res = await fetch(`${apiBaseUrl}/campaigns`, {
          headers: { 'x-user-id': clerkUserId },
        });
        if (!res.ok) return;
        const data = await res.json();
        const allCampaigns = Array.isArray(data) ? data : (data.campaigns || []);
        const projectCampaigns = allCampaigns.filter(c => c.project_id === projectId);
        setCampaigns(projectCampaigns.sort((a, b) => a.name.localeCompare(b.name)));
      } catch (_) {}
    };
    fetchCampaigns();
  }, [projectId, apiBaseUrl, clerkUserId]);

  // Determine if caller is an org admin
  useEffect(() => {
    if (!clerkUserId || !apiBaseUrl) return;
    const checkAdmin = async () => {
      try {
        const res  = await fetch(`${apiBaseUrl}/org/mine`, {
          headers: { 'x-user-id': clerkUserId }
        });
        const data = await res.json();
        if (!res.ok || !data.org) return;
        const isOwner = data.org.created_by_user_id === clerkUserId;
        const member  = (data.org.members || []).find(m => m.user_id === clerkUserId);
        setIsAdmin(isOwner || member?.role === 'admin');
      } catch (_) {}
    };
    checkAdmin();
  }, [clerkUserId, apiBaseUrl]);

  // Fetch history
  useEffect(() => {
    if (!scorecard || !apiBaseUrl || !clerkUserId) return;
    if (scorecard.totalCampaigns === 0) return;
    const fetchHistory = async () => {
      setHistoryLoading(true);
      try {
        const campaignsRes = await fetch(`${apiBaseUrl}/campaigns`, {
          headers: { 'x-user-id': clerkUserId },
        });
        if (!campaignsRes.ok) return;
        const allCampaigns = await campaignsRes.json();
        const projectCampaigns = Array.isArray(allCampaigns)
          ? allCampaigns.filter(c => c.project_id === projectId)
          : [];
        if (!projectCampaigns.length) return;
        const allRuns = [];
        await Promise.all(projectCampaigns.map(async (campaign) => {
          try {
            const res = await fetch(
              `${apiBaseUrl}/audit-results/campaign/${campaign.id}/history?moduleType=llm-audit&days=60`,
              { headers: { 'x-user-id': clerkUserId } }
            );
            if (!res.ok) return;
            const rows = await res.json();
            if (!Array.isArray(rows)) return;
            rows.forEach(row => {
              const metrics = extractRunMetrics(row.result_data, row.created_at);
              if (metrics) allRuns.push(metrics);
            });
          } catch (_) {}
        }));
        if (!allRuns.length) return;
        allRuns.sort((a, b) => new Date(a.date) - new Date(b.date));
        const byLabel = {};
        allRuns.forEach(run => {
          if (!byLabel[run.label]) byLabel[run.label] = [];
          byLabel[run.label].push(run);
        });
        const timeline = Object.entries(byLabel).map(([label, runs]) => {
          const avg = (key) => {
            const vals = runs.map(r => r[key]).filter(v => v !== null && v !== undefined);
            return vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : null;
          };
          return {
            label,
            visibility: avg('visibility'),
            citation:   avg('citation'),
            sentiment:  avg('sentiment'),
          };
        });
        setTimelineData(timeline);
        const mostRecent = allRuns[allRuns.length - 1];
        if (mostRecent && mostRecent.engines) {
          const bars = Object.entries(mostRecent.engines)
            .filter(([, v]) => v !== null)
            .map(([engine, value]) => ({
              engine: engine.charAt(0).toUpperCase() + engine.slice(1),
              engineKey: engine,
              visibility: Math.round(value),
            }));
          setEngineData(bars);
        }
      } catch (_) {
      } finally {
        setHistoryLoading(false);
      }
    };
    fetchHistory();
  }, [scorecard, apiBaseUrl, clerkUserId, projectId]);

  if (loading) {
    return (
      <div style={{ padding: '48px', textAlign: 'center', color: C.textMuted }}>
        <div style={{
          width: '32px', height: '32px',
          border: `3px solid ${C.border}`,
          borderTop: `3px solid ${C.secondary}`,
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 16px',
        }} />
        Loading project scorecard…
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '48px', textAlign: 'center', color: C.red_tx }}>
        <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>Failed to load scorecard</div>
        <div style={{ fontSize: '14px', color: C.textMuted }}>{error}</div>
      </div>
    );
  }

  if (!scorecard) return null;

  const s = scorecard;
  const hasChartData  = timelineData.length >= 2;
  const hasEngineData = engineData.length > 0;
  const initials = getInitials(project?.name);

  // Metric items for horizontal overview
  const metricItems = [
    { key: 'visibility', label: 'VISIBILITY', type: 'visibility', deltaKey: 'llm-audit' },
    { key: 'citation', label: 'CITATION', type: 'citation', deltaKey: 'llm-audit-citation' },
    { key: 'position', label: 'POSITION', type: 'position', deltaKey: null },
    { key: 'content', label: 'CONTENT', type: 'content', deltaKey: null },
    { key: 'technical', label: 'TECHNICAL', type: 'technical', deltaKey: 'schema-audit' },
    { key: 'entityGap', label: 'ENTITY GAP', type: 'entityGap', deltaKey: 'content-graph', invertColors: true },
    { key: 'knowledgeGraph', label: 'KNOWLEDGE GRAPH', type: 'knowledgeGraph', deltaKey: 'knowledge-graph' }
  ];

  return (
    <div>
      {/* ── Minimal header: Icon roundel + project name (inline, matches mockup) ── */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: 'white',
          borderBottom: `1px solid ${C.border}`,
          padding: '16px 32px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
        }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}>
          {/* Icon roundel with project initials */}
          <div
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: C.secondary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px',
              fontWeight: '700',
              color: 'white',
              flexShrink: 0,
            }}
          >
            {initials}
          </div>
          <div style={{
            fontSize: '16px',
            fontWeight: '600',
            color: C.primary,
          }}>
            {project?.name || 'Project'}
          </div>
        </div>
      </div>

      {/* ── Horizontal metrics overview ── */}
      <div
        style={{
          backgroundColor: 'white',
          borderBottom: `1px solid ${C.border}`,
          padding: '20px 32px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
        }}
      >
        <div style={{ marginBottom: '12px' }}>
          <h2 style={{
            fontSize: '11px',
            fontWeight: '600',
            color: C.textMuted,
            textTransform: 'uppercase',
            letterSpacing: '0.7px',
            margin: 0,
          }}>
            Project Overview
          </h2>
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: '20px',
          width: '100%',
          overflowX: 'auto',
          paddingBottom: '8px',
        }}>
          {metricItems.map(({ key, label, type, deltaKey, invertColors }) => (
            <div key={key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flexShrink: 0 }}>
              <div
                style={{
                  fontSize: '28px',
                  fontWeight: '700',
                  marginBottom: '4px',
                  color: getScoreColor(s[key], type),
                }}
              >
                {formatMetricValue(s[key], type)}
              </div>
              <div style={{ fontSize: '11px', fontWeight: '600', color: C.textMuted, marginBottom: '4px' }}>
                {label}
              </div>
              {deltaKey && deltas[deltaKey] && (
                <DeltaBadge
                  delta={deltas[deltaKey]?.delta ?? null}
                  suffix="%"
                  invertColors={invertColors}
                />
              )}
            </div>
          ))}
          {/* Last Updated */}
          <div style={{ marginLeft: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <div style={{ fontSize: '11px', color: C.textMuted }}>Last updated</div>
            <div style={{ fontSize: '11px', fontWeight: '600', color: C.textMuted, marginTop: '4px' }}>
              {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
            </div>
          </div>
        </div>
      </div>

      {/* ── Main content ── */}
      <div style={{ padding: '32px 32px' }}>
        {/* ── Row 1: Project Stats (L) | Performance (R) ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', marginBottom: '40px' }}>
          {/* Left: Project Stats */}
          <div>
            <SectionLabel>Project</SectionLabel>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
              <Tile label="Campaigns"      value={s.totalCampaigns} isCount />
              <Tile label="Campaigns Live" value={s.campaignsLive}  isCount />
              <Tile label="URLs"           value={s.totalUrls}      isCount />
              <Tile label="Prompts"        value={s.totalPrompts}   isCount />
              <Tile
                label="Entity Bank"
                value={s.entityBank}
                isCount
                onClick={() => navigate(`/project/${projectId}/discovery`)}
                style={{ gridColumn: '1 / -1' }}
              />
            </div>
          </div>

          {/* Right: Performance */}
          <div>
            <SectionLabel>Performance</SectionLabel>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
              <Tile label="Visibility"   value={s.visibility} suffix="%" />
              <Tile label="Citation"     value={s.citation}    suffix="%" />
              <Tile label="Avg Position" value={s.position}    suffix="" invert={true} />
              <Tile label="Error Rate"   value={s.errorPct}    suffix="%" invert={true} />
              <Tile label="KGIS"         value={s.kgis}        suffix="" style={{ gridColumn: '1 / -1' }} />
            </div>
          </div>
        </div>

        {/* ── Row 2: Campaign Cards Grid (L) | Trends Chart (R) ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', marginBottom: '40px' }}>
          {/* Left: Campaign Cards Grid */}
          <div>
            {campaigns.length > 0 && (
              <>
                <SectionLabel>Campaigns · {campaigns.length}</SectionLabel>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '12px',
                }}>
                  {campaigns.map((campaign, i) => (
                    <CampaignCard
                      key={campaign.id || i}
                      campaign={campaign}
                      onClick={() => navigate(`/campaign/${campaign.id}`)}
                    />
                  ))}
                </div>
              </>
            )}
            {s.totalCampaigns === 0 && (
              <div style={{
                padding: '24px',
                backgroundColor: 'white',
                border: `1px solid ${C.border}`,
                borderRadius: '10px',
                textAlign: 'center',
                color: C.textMuted,
              }}>
                <div style={{ fontSize: '15px', fontWeight: '600', marginBottom: '8px', color: C.primary }}>
                  No campaigns yet
                </div>
                <div style={{ fontSize: '14px' }}>
                  Complete ENQ discovery and build your first campaign to see performance scores here.
                </div>
              </div>
            )}
          </div>

          {/* Right: Trends Chart */}
          {(hasChartData || historyLoading) && (
            <div>
              <SectionLabel>Trends – aggregate across all campaigns</SectionLabel>
              <div style={{
                backgroundColor: 'white',
                border: `1px solid ${C.border}`,
                borderRadius: '10px',
                padding: '24px',
              }}>
                {historyLoading && !hasChartData ? (
                  <div style={{ textAlign: 'center', color: C.textMuted, fontSize: '13px', padding: '32px' }}>
                    Loading trend data…
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={timelineData} margin={{ top: 4, right: 16, bottom: 4, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                      <XAxis
                        dataKey="label"
                        tick={{ fontSize: 11, fill: C.textMuted }}
                        tickLine={false}
                        axisLine={{ stroke: C.border }}
                      />
                      <YAxis
                        domain={[0, 100]}
                        tick={{ fontSize: 11, fill: C.textMuted }}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={v => `${v}%`}
                        width={36}
                      />
                      <Tooltip content={<TimelineTooltip />} />
                      <Legend
                        iconType="circle"
                        iconSize={8}
                        wrapperStyle={{ fontSize: '12px', paddingTop: '12px' }}
                      />
                      <Line
                        type="monotone"
                        dataKey="visibility"
                        name="Visibility"
                        stroke={CHART.visibility}
                        strokeWidth={2}
                        dot={{ r: 3, fill: CHART.visibility }}
                        activeDot={{ r: 5 }}
                        connectNulls
                      />
                      <Line
                        type="monotone"
                        dataKey="citation"
                        name="Citation"
                        stroke={CHART.citation}
                        strokeWidth={2}
                        dot={{ r: 3, fill: CHART.citation }}
                        activeDot={{ r: 5 }}
                        connectNulls
                      />
                      <Line
                        type="monotone"
                        dataKey="sentiment"
                        name="Sentiment"
                        stroke={CHART.sentiment}
                        strokeWidth={2}
                        dot={{ r: 3, fill: CHART.sentiment }}
                        activeDot={{ r: 5 }}
                        connectNulls
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ── Engine breakdown bar chart (full width) ── */}
        {hasEngineData && (
          <>
            <SectionLabel>Visibility by Engine</SectionLabel>
            <div style={{
              backgroundColor: 'white',
              border: `1px solid ${C.border}`,
              borderRadius: '10px',
              padding: '24px',
              marginBottom: '40px',
            }}>
              <ResponsiveContainer width="100%" height={160}>
                <BarChart
                  data={engineData}
                  margin={{ top: 4, right: 16, bottom: 4, left: 0 }}
                  barSize={40}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
                  <XAxis
                    dataKey="engine"
                    tick={{ fontSize: 12, fill: C.textMuted, fontWeight: 600 }}
                    tickLine={false}
                    axisLine={{ stroke: C.border }}
                  />
                  <YAxis
                    domain={[0, 100]}
                    tick={{ fontSize: 11, fill: C.textMuted }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={v => `${v}%`}
                    width={36}
                  />
                  <Tooltip content={<EngineTooltip />} cursor={{ fill: C.lightbg }} />
                  <Bar
                    dataKey="visibility"
                    name="Visibility"
                    radius={[4, 4, 0, 0]}
                    fill={C.secondary}
                  />
                </BarChart>
              </ResponsiveContainer>
              <div style={{ display: 'flex', gap: '20px', marginTop: '12px', flexWrap: 'wrap' }}>
                {engineData.map(e => (
                  <div key={e.engineKey} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: C.textMuted }}>
                    <span style={{
                      width: '10px', height: '10px', borderRadius: '2px',
                      backgroundColor: ENGINE_COLORS[e.engineKey] || C.secondary,
                      display: 'inline-block', flexShrink: 0,
                    }} />
                    {e.engine}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* ── Project members / client access (full width) ── */}
        <SectionLabel>Project access</SectionLabel>
        <ProjectMembersPanel
          projectId={projectId}
          apiBaseUrl={apiBaseUrl}
          isAdmin={isAdmin}
        />
      </div>
    </div>
  );
}

export default ProjectScorecard;
